import React, { useState } from "react";
import { alpha, Box, SxProps, Tooltip, useTheme } from "@mui/material";
import _ from "lodash";
import {
  getLeadStatusByStage,
  getProductData,
  getProductStageList,
} from "../../../services/Product/ProductService/productService";
import { validateStatusUpdate } from "../../../services/StatusChangeValidators/statusChangeValidators";
import StatusValidationErrors from "./StatusValidationErrors";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import { SubmissionError } from "../../../services/ApiClient";
import LegalbirdIoModal from "../../Modal/LegalbirdIoModal";
import { useMutation } from "@tanstack/react-query";
import { updateResource } from "../../../services/ReactQuery/reactQueryService";
import CaseSubBarInfo from "../CaseSubBarInfo/CaseSubBarInfo";
import { ProductStage } from "../../../types/ProductStage";
import { StatusValidationError } from "../../../types/StatusValidationError";
import { ProductUnion } from "../../../types/ProductUnion";
import dayjs from "dayjs";

type CaseProgressBarProps = {
  product: ProductUnion;
  refreshPage: Function;
};

const CaseProgressBar = ({ product, refreshPage }: CaseProgressBarProps) => {
  const theme = useTheme();
  const updateMutation = useMutation({
    mutationFn: updateResource
  });
  const [error, setError] = useState<StatusValidationError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [dialogStage, setDialogStage] = useState<ProductStage>();
  const stageList: ProductStage[] = getProductStageList(product);

  const getStageSx = (product: ProductUnion, stage: ProductStage) => {
    let stageSx: SxProps = {
      flexBasis: 0,
      flexGrow: 1,
      height: 30,
      backgroundColor: "border.inActive",
      margin: "0 5px 0 0",
      padding: "0.25rem",
      textAlign: "center",
    };

    if (stage.isActive({ product }) && stage.isClickable({ product })) {
      stageSx = {
        ...stageSx,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        "&:hover": {
          boxShadow: "0px 0px 10px 0px rgba(23,107,103,1)",
          cursor: "pointer",
        },
      };
    }

    if (stage.stage <= product.stage) {
      stageSx = {
        ...stageSx,
        ...(product.backofficeCase.status === "lost"
          ? {
              backgroundColor: alpha(theme.palette.error.light, 0.6),
            }
          : {
              backgroundColor: alpha(theme.palette.primary.light, 0.6),
            }),
      };

      return stageSx;
    }

    if (!stage.isActive({ product })) {
      stageSx = {
        ...stageSx,
        backgroundColor: "grey.100",
      };
    }

    return stageSx;
  };

  const handleCloseErrorDialog = () => {
    setError(null);
  };

  const handleStageUpdate = async (stage: ProductStage) => {
    setIsLoading(true);
    if (!stage.isClickable({ product })) {
      setIsLoading(false);
      return;
    }

    const errors = validateStatusUpdate(product, stage.stage);
    if (errors.length > 0) {
      setError({
        stage: stage.label,
        error: new SubmissionError(errors, 422),
      });
      setIsLoading(false);
      return;
    }

    await updateMutation.mutateAsync(
      {
        id: product.id,
        uri: getProductData(product.productClassName, "apiUri"),
        data: {
          leadStatus: getLeadStatusByStage(product.productClassName, stage.stage),
        },
      },
      {
        onError: (error) => {
          setError({
            stage: stage.label,
            error: error,
          });
          setIsLoading(false);
        },
        onSuccess: () => refreshPage(),
      }
    );
  };

  const handleClick = async (stage: ProductStage) => {
    if (
      !stage.isClickable({ product }) ||
      !stage.isActive({ product }) ||
      stage.stage < getProductData(product.productClassName, "setBackCaseStage")
    ) {
      return;
    }

    if (
      product.stage &&
      stage.stage < product.stage &&
      stage.stage >= getProductData(product.productClassName, "setBackCaseStage")
    ) {
      setDialogStage(stage);
      setConfirmationDialogOpen(true);
      return;
    }

    await handleStageUpdate(stage);
  };

  if (!stageList) {
    return null;
  }

  const calculatedStageLog = calculateStageLog(product.stageLog);

  return (
    <>
      <Box sx={{ display: { xs: "none", lg: "block" } }}>
        {error && (
          <StatusValidationErrors error={error.error} stage={error.stage} closeDialog={handleCloseErrorDialog} />
        )}
        <Box sx={{ whiteSpace: "nowrap", margin: "1rem 0 1rem 0", display: "flex" }}>
          {_.map(stageList, (stage) => (
            <Tooltip key={stage.stage} title={stage.label} onClick={() => handleClick(stage)}>
              <Box sx={getStageSx(product, stage)}>
                {isLoading ? <CircularProgress size={20} /> : calculatedStageLog[stage.stage]}
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>
      <CaseSubBarInfo product={product} />
      <LegalbirdIoModal
        handleClose={() => setConfirmationDialogOpen(false)}
        open={confirmationDialogOpen}
        title={"Den Status wirklich zurücksetzen?"}
        submitButton={
          <Button
            variant={"contained"}
            onClick={() => {
              setConfirmationDialogOpen(false);
              if (!dialogStage) {
                return;
              }
              handleStageUpdate(dialogStage);
            }}
          >
            Status zurücksetzen
          </Button>
        }
      >
        <p className={"center"}>
          Dies kann zu unerwünschten Effekten führen wie zum Beispiel zum Versenden von E-Mails oder mehrfaches Anlegen
          von Aktivitäten!
        </p>
      </LegalbirdIoModal>
    </>
  );
};

function calculateStageLog(stageLog: StageLog) {
  const result: StageLog = {};

  Object.keys(stageLog).forEach((currentStage, index, allStagesInStatusLog) => {
    const currentStageAsNumber = parseInt(currentStage);
    const startDate = dayjs(stageLog[currentStageAsNumber]);

    const nextStageIndex = parseInt(allStagesInStatusLog[index + 1]);
    const nextStage = stageLog[nextStageIndex];

    if (nextStage) {
      const diff = startDate.diff(dayjs(nextStage), "days");
      const days = Math.abs(diff);
      result[currentStageAsNumber] = `${days} Tag${days === 1 ? "" : "e"}`;
    } else {
      const diff = startDate.diff(dayjs(), "days");
      const days = Math.abs(diff);
      result[currentStageAsNumber] = days === 1 ? "seit einem Tag" : `seit ${days} Tagen`;
    }
  });

  return result;
}

type StageLog = {
  [key: number]: string;
};

export default CaseProgressBar;
